import { Injectable } from '@angular/core';
import {
    CanActivate,
    CanActivateChild,
    ActivatedRouteSnapshot,
    Router,
    RouterStateSnapshot,
    UrlTree,
    NavigationStart
} from '@angular/router';
import { AppLocaleService } from '../../services/app-locale/app-locale.service';
import { CONSTANTS } from '../locale-route.constants';
import { filter } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class LocaleGuard implements CanActivate, CanActivateChild {
    private currentLocale: string = '';
    hasPreviousPathLocale: boolean = false;

    constructor(private router: Router, private localeService: AppLocaleService) {
        // Listen event before go to next route
        this.router.events
            .pipe(filter((event) => event instanceof NavigationStart))
            .subscribe(() => this.validateLocaleInPreviousPath(this.router.url));
    }

    /**
     * Check locale from URL:
     * If exists and is not valid redirect to error page
     * If exists and is valid activate route
     * If not exists locale from URL checks locale from cookie:
     *  - If locale from cookie is equal to default locale activate route
     *  - If not exists locale from previous route path activate route
     *  - If locale from cookie is not equal to default locale:
     *    add locale from cookie to URL and redirect
     * @param route - ActivatedRouteSnapshot
     * @param state - RouterStateSnapshot
     * @return boolean or UrlTree
     */
    private checkLocale(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
        const localeFromUrl = route.paramMap.get('locale');
        this.currentLocale = this.localeService.content().toLowerCase().replace('_', '-');
        const urlParts = state.url.split('/');

        if (localeFromUrl) {
            if (!CONSTANTS.VALID_LOCALE_REG_EXP.test(localeFromUrl)) {
                this.router.navigate(['/error/404']);

                return false;
            }

            return true;
        }

        if (!this.hasPreviousPathLocale) {
            return true;
        }

        urlParts.splice(1, 0, this.currentLocale);

        return this.parseUrl(urlParts);
    }

    /**
     * Parse url from url parts
     * @param urlParts - Array with url parts
     * @return UrlTree
     */
    private parseUrl(urlParts: string[]) {
        const newUrl = urlParts.join('/');

        return this.router.parseUrl(newUrl);
    }

    /**
     * Validate if previous route path has locale param
     * @param previousPath - Previous url path
     */
    private validateLocaleInPreviousPath(previousPath: string) {
        let urlParts = previousPath?.split('/');
        const localeParam = urlParts?.[1];

        this.hasPreviousPathLocale = CONSTANTS.VALID_LOCALE_REG_EXP.test(localeParam);
    }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
        return this.checkLocale(route, state);
    }

    canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
        return this.checkLocale(route, state);
    }
}
